import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, KeyRound } from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatAuditDate } from "../lib/playerHealth";
import { resolvePasskeyAddErrorMessage, resolvePasskeyRemoveErrorMessage } from "../lib/settingsErrorMessages";
import { useAuth } from "../context/AuthContext";
import { MoreCardHeader } from "./SettingsMoreCard";

/**
 * "Passkeys" — enrolment and management for the signed-in account (#560).
 *
 * An AccountProfileCard SIBLING, mounted directly under it in Settings'
 * decided order: a passkey is a sign-in credential, so it belongs in the
 * identity group, but it is its own card rather than another section
 * inside the merged Account card — that card already absorbed two cards
 * under a product decision (#456) and adding a third concern is how it
 * stops being readable. Always open, like its sibling: it answers "how do
 * I get into this account", not an accumulating log.
 *
 * WHAT THIS CARD DOES NOT DO (#510's later slices): sign IN with a
 * passkey, or offer one at signup. Login.jsx is untouched by #560.
 *
 * THE CONFINED STATE IS RENDERED HONESTLY, not offered-then-errored: a
 * `must_change_password` account (a provisioned admin before their first
 * change, or anyone an admin reset) sees WHY it cannot enrol and a
 * disabled control, mirroring the API's own 409 refusal
 * (`PASSKEY_ENROLMENT_CONFINED` — routes/passkeys.ts argues why a
 * confined account must not mint an unconfined door). The API stays the
 * enforcer; this card just declines to dangle a button that can only 409.
 *
 * BROWSER SUPPORT is feature-detected (`window.PublicKeyCredential`), and
 * an unsupported browser gets a sentence, not a broken ceremony. A
 * CANCELLED ceremony (the user dismissed the browser dialog —
 * `NotAllowedError`) is silence, not an error toast: dismissing a system
 * dialog was the user's own act and toasting it as failure reads as blame.
 *
 * The ceremony call is `@simplewebauthn/browser`'s `startRegistration`
 * over the server's options VERBATIM — this card never builds or edits
 * WebAuthn options, so the user-handle decision (opaque id, never email)
 * lives only on the server (#510), with a source-level test pinning that
 * no email reaches the ceremony from here.
 */
/**
 * `appearance` (issue #976, D186.10) — opt-in, default `undefined` (shipped).
 * D186.10: "Passkeys is drawn with no chevron or summary, so it stays always
 * open and restyled." Settings A mounted this card unchanged, inside the
 * drawn frame, because its own contents were never on screen there (folds
 * arrive closed); Settings B opens the page's other folds, which puts this
 * card's insides on screen too, so D194 (`settings-passkeys-empty` computing
 * to #334155, the platform-wide WCAG bump on `text-slate-600`, not a
 * declared token) is this file's to clear. `appearance="r5"`
 * (`SettingsScreen.jsx`'s only call) restyles, in place:
 *   - the outer frame's padding to the drawn 14px, the `SectionCard`
 *     `appearance="r5"` precedent;
 *   - "Add a passkey" ALWAYS NAVY (D195, #989 round 2 — supersedes an
 *     earlier gold read of D186.7/R-003 here). Passkeys never folds
 *     (D186.10), so its own commit can't be "the one gold" the closed
 *     page's Accept, or an opened fold's own commit, already claims —
 *     D64 stands: with no invite waiting, the closed page has no gold at
 *     all;
 *   - the empty/unsupported/confined sentences to `var(--nw-ink-lede)`
 *     (#475569, as drawn) — D194's fix, and #989 round 2's N1 (the
 *     confined sentence carried the same WCAG-bumped class), scoped so
 *     the shipped leg keeps its existing Tailwind class untouched;
 *   - Remove/Yes-remove-it to D55's destructive shapes (alert-outlined
 *     trigger, alert-filled confirm verb) — same precedence rule
 *     the former `AccountProfileCardNew` (#1135) states: this artboard draws both Cancel
 *     and the confirm as plain navy outline, but D55 wins where they
 *     differ. Cancel disarms, is not destructive, and keeps its navy
 *     outline unchanged;
 *   - add/remove failures render on the page (D57, #989 round 2 F4) via
 *     the shared `AlertCallout` shape, `settings-passkeys-error`, in the
 *     drawn words (`SettingsPasskeys.dc.html`'s own `add-failed` state:
 *     "Could not add a passkey on this device"; its commentary: "A failed
 *     remove says 'Remove failed' in the same place") — never a toast;
 *   - the "Added …"/"Last used …" dates use the same long-form
 *     (`formatAuditDate`, "30 September 2026") the health card already
 *     uses, not the browser-locale-default short form (#989 round 2, F7).
 * The shipped call (`pages/Settings.jsx`'s bare `<PasskeysCard />`, no
 * `appearance` at all) renders exactly as before — every branch below is
 * additive, never a default change.
 */
const ALERT = "#B42318";

/** D57's on-page failure shape (round 2 review, S-F1), the same ten-line
 * copy the former `AccountProfileCardNew` (#1135)/`CoachAccessCardNew.jsx` already carry —
 * kept as its own local copy rather than a shared import, matching this
 * codebase's own stated preference for two callers. */
function AlertCallout({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "#F1F5F9",
        borderLeft: `3px solid ${ALERT}`,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "#0F172A",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <Info size={16} color={ALERT} />
      </span>
      <span>{children}</span>
    </div>
  );
}

function ShippedFrame({ children }) {
  return (
    <Card className="border-slate-200" data-testid="settings-passkeys-card">
      <CardContent className="p-6 space-y-4">{children}</CardContent>
    </Card>
  );
}

function KitFrame({ children }) {
  return (
    <div
      className="flex flex-col"
      data-testid="settings-passkeys-card"
      style={{
        gap: 10,
        padding: 14,
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      {children}
    </div>
  );
}

export default function PasskeysCard({ appearance } = {}) {
  const r5 = appearance === "r5";
  const { user } = useAuth();
  const confined = !!user?.must_change_password;
  const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const [passkeys, setPasskeys] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  // Two-step remove, the sign-out-everywhere pattern: first press arms the
  // row, the destructive DELETE happens on a second, differently-worded
  // press. Removing a passkey can lock someone out of a passwordless habit.
  const [armedId, setArmedId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  // D57 (#989 round 2, F4) — one slot for either failure, matching
  // SettingsHealth.dc.html's own commentary shape ("a failed remove says
  // 'Remove failed' in the same place"). Only read under r5; the shipped
  // leg still toasts and never sets this.
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/auth/passkey");
      setPasskeys(data);
    } catch {
      // A confined account gets the generic 403 here; anyone else a
      // transient failure. Either way the list is simply unavailable —
      // the card's states below already say why when it matters.
      setPasskeys([]);
    }
    setLoaded(true);
  };

  useEffect(() => {
    if (!confined) load();
    else setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confined]);

  const addPasskey = async () => {
    setAdding(true);
    setError("");
    try {
      const { data: options } = await api.post("/auth/passkey/register/options", {});
      const ceremony = await startRegistration({ optionsJSON: options });
      await api.post("/auth/passkey/register/verify", ceremony);
      toast.success("Passkey added");
      await load();
    } catch (e) {
      if (e?.name === "NotAllowedError") {
        // User dismissed the browser dialog — their call, no toast.
      } else {
        // D57 (#989 round 2, F4): on the page under r5, drawn words,
        // AlertCallout shape, no toast. Shipped leg keeps the toast and
        // its own (unfixed, byte-identical) message computation —
        // `resolvePasskeyAddErrorMessage`'s O1 fix (#989 round 3) is
        // scoped to r5 only.
        if (r5) {
          setError(resolvePasskeyAddErrorMessage(e));
        } else {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add a passkey on this device");
        }
      }
    }
    setAdding(false);
  };

  const removePasskey = async (id) => {
    setRemovingId(id);
    setError("");
    try {
      await api.delete(`/auth/passkey/${id}`);
      toast.success("Passkey removed");
      await load();
    } catch (e) {
      // Same O1 scoping as addPasskey's own catch, above.
      if (r5) {
        setError(resolvePasskeyRemoveErrorMessage(e));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
      }
    }
    setRemovingId(null);
    setArmedId(null);
  };

  const formatDate = (iso) => {
    if (r5) return formatAuditDate(iso);
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
  };

  // #1135 (D246): under r5 the frame is the kit's surface (as
  // `pages/GuidePage.jsx` draws it), not the shipped shadcn `Card`. The
  // shipped call passes no `appearance` and keeps its Card.
  const Frame = r5 ? KitFrame : ShippedFrame;

  return (
    <Frame>
      {r5 ? (
        // #1135: the approved board's header for a card in More.
        <MoreCardHeader icon={KeyRound} title="Passkeys" subtitle="Sign in with your face or fingerprint." />
      ) : (
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
            <KeyRound size={16} color="#0B2E59" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg text-slate-900">Passkeys</h2>
            <p className="text-sm text-slate-500">
              Sign in with your face, fingerprint, or device PIN instead of typing anything.
            </p>
          </div>
        </div>
      )}

      {confined ? (
        // N1 (#989 round 2): var(--nw-ink-lede) under r5, not
        // text-slate-600 (the D194 class of bug — see this file's own
        // docstring).
        <p
          className={r5 ? "text-sm" : "text-sm text-slate-600"}
          style={r5 ? { color: "var(--nw-ink-lede)" } : undefined}
          data-testid="settings-passkeys-confined"
        >
          You need to change your password first — the one you signed in with was set by an
          administrator. Once it's changed, you can add passkeys here.
        </p>
      ) : !supported ? (
        // D194: `var(--nw-ink-lede)` (#475569, as drawn) under `r5`, not
        // `text-slate-600` (which the platform-wide WCAG bump renders
        // #334155 — a real colour, but not a declared token). The shipped
        // leg keeps its existing class untouched.
        <p
          className={r5 ? "text-sm" : "text-sm text-slate-600"}
          style={r5 ? { color: "var(--nw-ink-lede)" } : undefined}
          data-testid="settings-passkeys-unsupported"
        >
          This browser doesn't support passkeys. Try a current version of Chrome, Safari, Edge,
          or Firefox.
        </p>
      ) : (
        <>
          {loaded && passkeys.length === 0 && (
            <p
              className={r5 ? "text-sm" : "text-sm text-slate-600"}
              style={r5 ? { color: "var(--nw-ink-lede)" } : undefined}
              data-testid="settings-passkeys-empty"
            >
              No passkeys yet. Add one to sign in without a password on devices you trust.
            </p>
          )}

          {passkeys.length > 0 && (
            <ul className="space-y-3" data-testid="settings-passkeys-list">
              {passkeys.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-slate-200 p-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{p.name}</span>
                      {p.backed_up && (
                        <Badge variant="outline" className="text-xs font-normal text-slate-500">
                          Synced across your devices
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">
                      Added {formatDate(p.created_at)}
                      {p.last_used_at ? ` · Last used ${formatDate(p.last_used_at)}` : ""}
                    </div>
                  </div>
                  {armedId === p.id ? (
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Cancel disarms; it is not destructive and keeps
                          the navy outline either way (D55). */}
                      <Button
                        onClick={() => setArmedId(null)}
                        disabled={removingId === p.id}
                        variant="outline"
                        className="h-9"
                        data-testid={`settings-passkey-remove-cancel-${p.id}`}
                      >
                        Cancel
                      </Button>
                      {/* D55: the alert-FILLED verb under r5 — the second
                          press that actually deletes. Off r5, unchanged
                          (Tailwind's red-200/600 approximation). */}
                      <Button
                        onClick={() => removePasskey(p.id)}
                        disabled={removingId === p.id}
                        variant={r5 ? undefined : "outline"}
                        className={r5 ? "h-9" : "h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"}
                        style={r5 ? { background: ALERT, borderColor: ALERT, color: "#FFFFFF" } : undefined}
                        data-testid={`settings-passkey-remove-confirm-${p.id}`}
                      >
                        {removingId === p.id ? "Removing…" : "Yes, remove it"}
                      </Button>
                    </div>
                  ) : (
                    // D55: the alert-OUTLINED trigger under r5, the exact
                    // hex rather than Tailwind's red-200/600 approximation.
                    <Button
                      onClick={() => setArmedId(p.id)}
                      variant="outline"
                      className={r5 ? "h-9 shrink-0" : "h-9 shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"}
                      style={r5 ? { borderColor: ALERT, color: ALERT } : undefined}
                      data-testid={`settings-passkey-remove-${p.id}`}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* D195 (#989 round 2, F2): always navy. Passkeys never folds,
              so its commit can't be the page's one gold — D64 stands. */}
          <Button
            onClick={addPasskey}
            disabled={adding}
            variant={r5 ? "outline" : undefined}
            className={r5 ? "h-11" : "h-9"}
            style={r5 ? { alignSelf: "flex-start", background: "var(--nw-surface)", borderColor: "var(--nw-line-strong)", color: "var(--nw-navy)" } : { background: "#0B2E59" }}
            data-testid="settings-passkeys-add"
          >
            {adding ? "Follow your browser's prompts…" : "Add a passkey"}
          </Button>

          {/* D57 (#989 round 2, F4): add/remove failures render here under
              r5, never a toast. */}
          {r5 && error && <AlertCallout testId="settings-passkeys-error">{error}</AlertCallout>}
        </>
      )}
    </Frame>
  );
}
